import React, { Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { MainLayout } from './layouts/MainLayout'
import { AuthLayout } from './layouts/AuthLayout'
import { PortalLayout } from './layouts/PortalLayout'
import { ProtectedRoute } from './features/auth/components/ProtectedRoute'

// Lazy load page components for better performance
const Login = React.lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })))
const RequestAccess = React.lazy(() =>
  import('./pages/RequestAccess').then((m) => ({ default: m.RequestAccess }))
)
const SetupPassword = React.lazy(() =>
  import('./pages/SetupPassword').then((m) => ({ default: m.SetupPassword }))
)
const ForgotPassword = React.lazy(() =>
  import('./pages/ForgotPassword').then((m) => ({ default: m.ForgotPassword }))
)
const ResetPassword = React.lazy(() =>
  import('./pages/ResetPassword').then((m) => ({ default: m.ResetPassword }))
)
const AdminAccessRequests = React.lazy(() =>
  import('./pages/AdminAccessRequests').then((m) => ({ default: m.AdminAccessRequests }))
)
const Dashboard = React.lazy(() =>
  import('./pages/Dashboard').then((m) => ({ default: m.Dashboard }))
)
const JobDetail = React.lazy(() =>
  import('./pages/JobDetail').then((m) => ({ default: m.JobDetail }))
)
const CreateJobPage = React.lazy(() =>
  import('./pages/CreateJobPage').then((m) => ({ default: m.CreateJobPage }))
)
const NodeDetailPage = React.lazy(() =>
  import('./pages/NodeDetailPage').then((m) => ({ default: m.NodeDetailPage }))
)
const JobReports = React.lazy(() =>
  import('./pages/JobReports').then((m) => ({ default: m.JobReports }))
)
const CreateReport = React.lazy(() =>
  import('./pages/CreateReport').then((m) => ({ default: m.CreateReport }))
)
const ReportPreview = React.lazy(() =>
  import('./pages/ReportPreview').then((m) => ({ default: m.ReportPreview }))
)
const QuoteEditorPage = React.lazy(() =>
  import('./pages/QuoteEditorPage').then((m) => ({ default: m.QuoteEditorPage }))
)
const Settings = React.lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })))
const Notifications = React.lazy(() =>
  import('./pages/Notifications').then((m) => ({ default: m.Notifications }))
)
const Teams = React.lazy(() => import('./pages/Teams').then((m) => ({ default: m.Teams })))
const Profile = React.lazy(() => import('./pages/Profile').then((m) => ({ default: m.Profile })))
const Clients = React.lazy(() => import('./pages/Clients').then((m) => ({ default: m.Clients })))
const ClientEditorPage = React.lazy(() =>
  import('./pages/ClientEditorPage').then((m) => ({ default: m.ClientEditorPage }))
)
const OfflineMaps = React.lazy(() =>
  import('./pages/OfflineMaps').then((m) => ({ default: m.OfflineMaps }))
)
const Branding = React.lazy(() => import('./pages/Branding').then((m) => ({ default: m.Branding })))
const BrandPresetEditorPage = React.lazy(() =>
  import('./pages/BrandPresetEditorPage').then((m) => ({ default: m.BrandPresetEditorPage }))
)
const PortalLogin = React.lazy(() =>
  import('./pages/PortalLogin').then((m) => ({ default: m.PortalLogin }))
)
const PortalReports = React.lazy(() =>
  import('./pages/PortalReports').then((m) => ({ default: m.PortalReports }))
)
const PortalReportView = React.lazy(() =>
  import('./pages/PortalReportView').then((m) => ({ default: m.PortalReportView }))
)
const PortalQuotes = React.lazy(() =>
  import('./pages/PortalQuotes').then((m) => ({ default: m.PortalQuotes }))
)
const PortalQuoteView = React.lazy(() =>
  import('./pages/PortalQuoteView').then((m) => ({ default: m.PortalQuoteView }))
)
const QuoteRedirect = React.lazy(() =>
  import('./pages/EntityRedirect').then((m) => ({ default: m.QuoteRedirect }))
)
const ReportRedirect = React.lazy(() =>
  import('./pages/EntityRedirect').then((m) => ({ default: m.ReportRedirect }))
)

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center h-full min-h-[200px]">
    <div className="text-sm text-muted-foreground">Loading...</div>
  </div>
)

// Wrap lazy components with Suspense
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
)

// Placeholder page components (will be implemented in later phases)
const ReportViewer = () => <div>Report Viewer Page</div>
const AdminPanel = () => <div>Admin Panel Page</div>
const NotFound = () => <div>404 Not Found</div>

/**
 * Router configuration
 * - Defines all application routes
 * - Implements role-based access control
 * - Separates authenticated and public routes
 */
export const router = createBrowserRouter([
  // Public routes (authentication required)
  {
    path: '/',
    element: <AuthLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/login" replace />,
      },
      {
        path: 'login',
        element: withSuspense(Login),
      },
      {
        path: 'request-access',
        element: withSuspense(RequestAccess),
      },
      {
        path: 'setup-password',
        element: withSuspense(SetupPassword),
      },
      {
        path: 'forgot-password',
        element: withSuspense(ForgotPassword),
      },
      {
        // authService.resetPassword points its redirectTo here; this URL must
        // also be on Supabase's allowed redirect list, or the email falls
        // back to the project Site URL (see docs/SETUP.md)
        path: 'reset-password',
        element: withSuspense(ResetPassword),
      },
      {
        path: 'portal/login',
        element: withSuspense(PortalLogin),
      },
    ],
  },

  // Client portal (Phase 6): its own mobile-first shell, no arborist chrome
  {
    path: '/portal',
    element: (
      <ProtectedRoute allowedRoles={['client', 'admin']} redirectTo="/portal/login">
        <PortalLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: withSuspense(PortalReports),
      },
      {
        path: 'reports/:id',
        element: withSuspense(PortalReportView),
      },
      // Quotes (008 US2)
      {
        path: 'quotes',
        element: withSuspense(PortalQuotes),
      },
      {
        path: 'quotes/:id',
        element: withSuspense(PortalQuoteView),
      },
    ],
  },

  // Protected routes (authentication required)
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      // Dashboard (admin and arborist only)
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(Dashboard)}
          </ProtectedRoute>
        ),
      },

      // Jobs (admin and arborist only)
      {
        path: 'jobs/new',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(CreateJobPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'jobs/:id',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(JobDetail)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'jobs/:id/reports',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(JobReports)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'jobs/:id/reports/new',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(CreateReport)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'jobs/:id/reports/:reportId/preview',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(ReportPreview)}
          </ProtectedRoute>
        ),
      },
      // Quotes (008): a draft is created on /new and the editor takes over
      {
        path: 'jobs/:id/quotes/new',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(QuoteEditorPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'jobs/:id/quotes/:quoteId',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(QuoteEditorPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'jobs/:jobId/nodes/:nodeId',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(NodeDetailPage)}
          </ProtectedRoute>
        ),
      },

      // Clients (admin and arborist only)
      {
        path: 'clients',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(Clients)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'clients/new',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(ClientEditorPage)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'clients/:clientId',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(ClientEditorPage)}
          </ProtectedRoute>
        ),
      },

      // Branding (admin and arborist only)
      {
        path: 'branding',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(Branding)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'branding/:presetId',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(BrandPresetEditorPage)}
          </ProtectedRoute>
        ),
      },

      // Reports (admin and arborist only)
      {
        path: 'reports',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            <ReportViewer />
          </ProtectedRoute>
        ),
      },
      {
        // Notification deep link: resolves the job and forwards (008 R10)
        path: 'reports/:id',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(ReportRedirect)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'quotes/:id',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'arborist']}>
            {withSuspense(QuoteRedirect)}
          </ProtectedRoute>
        ),
      },

      // Admin panel (admin only)
      {
        path: 'admin',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPanel />
          </ProtectedRoute>
        ),
      },

      // Admin access requests (admin only)
      {
        path: 'admin/access-requests',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            {withSuspense(AdminAccessRequests)}
          </ProtectedRoute>
        ),
      },

      // Legacy client-portal path → new portal
      {
        path: 'client-portal',
        element: <Navigate to="/portal" replace />,
      },

      // Profile and settings (all authenticated users)
      {
        path: 'profile',
        element: withSuspense(Profile),
      },
      {
        path: 'settings',
        element: withSuspense(Settings),
      },
      {
        path: 'notifications',
        element: withSuspense(Notifications),
      },
      {
        path: 'teams',
        element: withSuspense(Teams),
      },
      {
        // Pre-002 bookmarks: the page lived at /organisation until slice 5
        path: 'organisation',
        element: <Navigate to="/teams" replace />,
      },
      {
        path: 'offline-maps',
        element: withSuspense(OfflineMaps),
      },
    ],
  },

  // 404 catch-all
  {
    path: '*',
    element: <NotFound />,
  },
])
